import { useGraphql } from 'utils/hooks/useGraphql';
import { BasicFilterSelectField } from 'lib/iterable-hook-form/FormField';
import debounce from 'lodash/debounce';
import { CallWebhookNodeData } from 'modules/workflows/types';
import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Link } from 'ui/links';
import { StyledLeftTooltip } from '../../../EditorBody.styled';
import { RESOURCE_FIELD, WEBHOOK_FIELD } from '../CallWebhookEditor.constants';
import { Content, DropdownWrapper } from '../CallWebhookEditor.styled';
import { defaultVariables, fetchWorkflowWebhooksQuery } from './WebhookSelect.graphql';

type Webhook = {
  id: number;
  name: string;
};

type FetchWebhookResponse = {
  workflowWebhooks?: {
    results?: Webhook[];
  };
};

const mapOptions = (results: Webhook[]): any =>
  results
    .map((webhook: Webhook) => ({
      id: `${webhook.id}`,
      label: webhook.name,
      value: webhook.id,
    }))
    .reverse(); // `sortDirection` variable on query does not seem to work properly :/

export const WebhookSelect = ({
  clonedFromNodeData,
  hasCloningError,
  isReadOnly,
  nodeData,
}: {
  clonedFromNodeData?: CallWebhookNodeData;
  hasCloningError: boolean;
  isReadOnly: boolean;
  nodeData: CallWebhookNodeData;
}) => {
  const { register, setValue, trigger } = useFormContext();
  const webhookId = useWatch({ name: `nodeData.${WEBHOOK_FIELD}` });

  const { data, isLoading, refetch } = useGraphql<FetchWebhookResponse>(
    fetchWorkflowWebhooksQuery,
    {
      variables: defaultVariables,
    },
  );
  const results = data?.workflowWebhooks?.results || [];

  // update dropdown options when results change
  const getOptions = () => useMemo(() => mapOptions(results), [results]);

  // refetch webhooks on search change
  const handleSearchChange = (st: string | undefined) =>
    refetch({
      ...defaultVariables,
      search: st,
    });

  useEffect(() => {
    // register the hidden resource name value so the default value can be used
    register(`nodeData.${RESOURCE_FIELD}`);
  }, []);

  useEffect(() => {
    // trigger validation if tile is cloned
    if (clonedFromNodeData?.[WEBHOOK_FIELD]) trigger(`nodeData.${WEBHOOK_FIELD}`);
  }, [clonedFromNodeData]);

  useEffect(() => {
    if (webhookId && results?.length) {
      const selected = results.find((opt: Webhook) => opt?.id === webhookId);
      // set resource value needed to display on canvas tile
      if (selected) setValue(`nodeData.${RESOURCE_FIELD}`, selected.name);
    }
  }, [webhookId]);

  return (
    <Content>
      <DropdownWrapper>
        <StyledLeftTooltip isEnabled={isReadOnly} tooltipContent={nodeData?.resource?.name}>
          <BasicFilterSelectField
            disableInternalFiltering
            id="webhook-select"
            isDisabled={isReadOnly}
            isFullWidth
            isLoading={isLoading}
            name={`nodeData.${WEBHOOK_FIELD}`}
            onSearchChange={debounce(handleSearchChange, 300)}
            options={getOptions()}
            placeholder={
              (hasCloningError && clonedFromNodeData?.[RESOURCE_FIELD]?.name) ||
              'Select a webhook...'
            }
          />
        </StyledLeftTooltip>
      </DropdownWrapper>
      <Link hasOpenNewTabIcon href="/workflows/webhooks" target="_blank" rel="noreferrer noopener">
        Manage webhooks
      </Link>
    </Content>
  );
};
